import React, { useMemo, useState } from 'react'
import { isEmpty, isNil, startCase } from 'lodash'
import classNames from 'classnames'
import { Field } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { IconSparkles, IconChevronRight, IconInfoCircle } from '@tabler/icons-react'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'

import { Loader } from '@/components/loaders/Loader'
import { openModal } from '@/reducers/ui/ui.redux'

import {
  TextField,
  TextArea,
  NumberField,
  Select,
  RadioGroup,
  MultiSelect,
} from '@/components/forms/formik'
import {
  createPromptTest,
  deletePromptTest,
  getPromptTestResults,
  getSinglePromptTestResults,
} from '@/reducers/prompts/prompts.actions'
import { ExternalLink } from '@/components/ExternalLink'
import { Banner } from '@/components/banners/Banner'
import { Pill } from '@/components/pills/Pill'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { DescriptionField } from '@/views/QACopilot/components/DescriptionField'
import { TimestampSilenceFields } from '@/views/QACopilot/components/TimestampSilenceFields'
import { LabelWithHelp } from '@/views/QACopilot/components/LabelWithHelp'
import { criteriaTypeOptions } from '@/views/QACopilot/QACopilot.helpers'
import { hasAnyOmnichannelAccess } from '@/utils/helpers'
import { LlmModelSelect } from '@/components/forms/formik/selects/LlmModelSelect'
import {
  getSelectableModelOptions,
  criterionModelBlocksSave,
} from '@/components/forms/formik/selects/modelGating'
import { ModelDeprecatedNotice } from '@/components/forms/formik/selects/ModelDeprecatedNotice'
import QACopilotPromptGenerationModal from '@/views/QACopilot/modals/QACopilotPromptGenerationModal'
import { OptimizePromptButton } from '@/views/QACopilot/optimize/OptimizePromptButton'

const CollapsibleSection = ({ id, title, expanded, onToggle, flush, children }) => (
  <div className={classNames('copilot-prompt__collapsible', { flush })}>
    <button
      type="button"
      className="copilot-prompt__collapsible-header"
      aria-expanded={expanded}
      onClick={() => onToggle(id)}
    >
      <span className="copilot-prompt__collapsible-title">{title}</span>
      <span className="copilot-prompt__collapsible-right">
        <IconChevronRight
          className={classNames('icon-svg copilot-prompt__chevron', { expanded })}
        />
      </span>
    </button>
    {expanded && <div className="copilot-prompt__collapsible-body">{children}</div>}
  </div>
)

const QACopilotScorecardCriteriaPromptFormComponent = ({
  handleSubmit,
  handleCancel,
  values,
  errors,
  isTemporaryCriteria,
  setFieldValue,
  flags,
  isOutdatedScorecard,
  // Passed through for prompt optimization: `scid` (the criterion LINEAGE) attributes an adoption after the
  // eventual save, `prompt_id` is what a kept test case hangs off, and `id` — present only for an already-saved
  // criterion — lets the sampler reuse production's stored answers instead of re-running the prompt.
  criteria,
  // The criterion's SAVED prompt, so optimization can tell whether the editor still holds it. When it does, the run
  // measures that record rather than saving a new one — which is what makes production's stored answers findable.
  savedPrompt,
}) => {
  const dispatch = useDispatch()
  const {
    loading: { promptTests: promptTestsLoading, modelOptions: modelOptionsLoading },
    prompts,
    modelOptions: {
      qa_copilot: qaCopilotModelOptions = [],
      copilot_fallback: copilotFallbackModelOptions = [],
    } = {},
  } = useSelector((state) => state.prompts)
  const [copilotTestResponse, setCopilotTestResponse] = useState(null)
  const [promptMode, setPromptMode] = useState('self')
  const [expandedSections, setExpandedSections] = useState(isTemporaryCriteria ? [] : ['testing'])
  const { organizationid: organizationId, prompt_editor: isPromptEditor } = useSelector(
    (state) => state.currentUser
  )
  const { scorecardConfig } = useSelector((state) => state.qaCopilot)
  const { focusArea } = useSelector((state) => state.qaCopilot)
  const isBaltoAdmin = organizationId === 1
  const isPromptTestingDisabled = !values.prompt_id
  const isPromptPairEmpty = isEmpty(values.call_id) || isEmpty(values.expected_response)
  const prompt = prompts.find((prompt) => values.prompt_id && prompt.id === values.prompt_id)
  const promptTests = prompt?.prompt_tests || []
  const canEditPrompts = isBaltoAdmin || isPromptEditor
  const promptsEnabled =
    canEditPrompts && (values.criteria_type === 'ai' || values.manual_timestamp_markers === 'true')
  const promptTestsEnabled = canEditPrompts && values.criteria_type === 'ai'
  const organizations = useSelector((state) => state.organizations)
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const isOmniChannelEnabled = useMemo(() => {
    if (isBaltoAdmin) {
      const scorecardConfigOrganization = organizations?.find(
        (org) => org.id === scorecardConfig?.organization_id
      )
      return hasAnyOmnichannelAccess(scorecardConfigOrganization?.active_channels)
    }
    return hasAnyOmnichannelAccess(activeChannels)
  }, [scorecardConfig?.organization_id])

  const selectedChannel = isOmniChannelEnabled ? scorecardConfig?.call_type : 'call'
  const isChannelMissing = isOmniChannelEnabled && !selectedChannel
  const isPromptTestDisabled =
    isPromptTestingDisabled || isPromptPairEmpty || isOutdatedScorecard || isChannelMissing

  const showGeneratePanel = isBaltoAdmin && values.criteria_type === 'ai' && promptMode === 'ai'
  const showsCoachingFields =
    !isEmpty(values.tasks_created_on_miss) &&
    values.tasks_created_on_miss.some((task) => task.value === 'coaching')

  // A deprecated model can't be saved (the server 422s), so block it in the UI. Only the
  // deprecated flag gates anything — hidden from new criteria, blocked here on edit.
  const modelIsDeprecated = criterionModelBlocksSave({
    criteriaType: values.criteria_type,
    manualTimestampMarkers: values.manual_timestamp_markers,
    options: qaCopilotModelOptions,
    value: values.llm_model_id,
  })

  const handleToggleSection = (sectionId) => {
    setExpandedSections((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId]
    )
  }

  const handleSavePromptTest = async () => {
    dispatch(
      createPromptTest(prompt, {
        inputs: {
          call_id: values.call_id,
          call_type: selectedChannel,
        },
        expected_response: values.expected_response,
        prompt_uuid: prompt.prompt_uuid,
        description: values.test_description,
      })
    )

    setFieldValue('call_id', '')
    setFieldValue('expected_response', '')
    setFieldValue('test_description', '')
  }

  const handleDeletePromptTest = (promptTestId) => {
    dispatch(deletePromptTest(prompts, prompt, promptTestId))
  }

  const handleSinglePromptTest = async (
    promptId,
    expectedResponse,
    callId,
    description,
    callType
  ) => {
    const systemPromptAdjusted = (() => {
      const base = values.system_prompt || ''
      if (callType === 'email') {
        return base.replace(/phone conversation/gi, 'email conversation')
      }
      return base
    })()
    const response = await dispatch(
      getSinglePromptTestResults(
        promptId,
        {
          user_prompt: values.user_prompt,
          llm_model_id: values.llm_model_id,
          feature: 'qa_copilot_test',
          system_prompt: systemPromptAdjusted,
          first_x_minutes: values.first_x_minutes || null,
          last_x_minutes: values.last_x_minutes || null,
          side: values.side,
          include_timestamps: values.include_timestamps === 'true',
          silence_threshold_seconds: values.silence_threshold_seconds || null,
          max_response_tokens: values.max_response_tokens,
          scores_to_fallback: values.scores_to_fallback?.map((score) => score.value),
          fallback_initial_model_id: values.fallback_initial_model_id,
          inputs: { call_type: callType },
        },
        expectedResponse,
        callId,
        description
      )
    )

    if (response) {
      setCopilotTestResponse([response])
    }
  }

  const handleTestCopilotPrompt = async () => {
    const systemPromptAdjusted = (() => {
      const base = values.system_prompt || ''
      if (selectedChannel === 'email') {
        return base.replace(/phone conversation/gi, 'email conversation')
      }
      return base
    })()
    const response = await dispatch(
      getPromptTestResults(values.prompt_id, {
        user_prompt: values.user_prompt,
        llm_model_id: values.llm_model_id,
        feature: 'qa_copilot_test',
        system_prompt: systemPromptAdjusted,
        first_x_minutes: values.first_x_minutes || null,
        last_x_minutes: values.last_x_minutes || null,
        side: values.side,
        include_timestamps: values.include_timestamps === 'true',
        silence_threshold_seconds: values.silence_threshold_seconds || null,
        max_response_tokens: values.max_response_tokens,
        scores_to_fallback: values.scores_to_fallback?.map((score) => score.value),
        fallback_initial_model_id: values.fallback_initial_model_id,
        inputs: { call_type: selectedChannel },
      })
    )

    setCopilotTestResponse(response)
  }

  const getActualResponse = (actualResponse) => {
    try {
      return JSON.stringify(JSON.parse(actualResponse), null, 2)
    } catch (err) {
      return actualResponse
    }
  }

  const handleOpenPromptGenerationModal = () => {
    dispatch(openModal('qaCopilot/promptGeneration'))
  }
  const formatInteractionId = (promptTest) => {
    if (promptTest.inputs?.call_type && promptTest.inputs?.call_type !== 'call') {
      return promptTest.inputs?.message_id
    }
    return promptTest.inputs?.call_id
  }

  const testingSection = (flush) =>
    promptTestsEnabled ? (
      <CollapsibleSection
        id="testing"
        title="Testing"
        flush={flush}
        expanded={expandedSections.includes('testing')}
        onToggle={handleToggleSection}
      >
        <div className="copilot-prompt__additional-details">
          <div>
            <div className="label-style">{startCase(selectedChannel)} IDs</div>
            <div className="copilot-prompt-pair">
              <Field
                name="call_id"
                component={TextField}
                placeholder={selectedChannel === 'email' ? 'Enter a Message ID' : 'Enter a Call ID'}
                className="call-id-field"
                autoFocus={!isTemporaryCriteria && focusArea === 'callid'}
                disabled={isOutdatedScorecard}
              />
              <div>.....</div>
              <Field
                name="expected_response"
                component={TextField}
                placeholder="Expected response"
                disabled={isOutdatedScorecard}
              />
              <div>.....</div>
              <Field
                name="test_description"
                component={TextField}
                placeholder="Add test description"
                disabled={isOutdatedScorecard}
              />
              <Button
                onClick={handleSavePromptTest}
                disabled={isPromptTestDisabled}
                disabledTooltip={
                  isPromptPairEmpty
                    ? 'You must add a call id and an expected response'
                    : 'You must save the scorecard before setting up prompt testing'
                }
              >
                Add Pair
              </Button>
              <Button
                brand
                type="button"
                onClick={() =>
                  handleSinglePromptTest(
                    values.prompt_id,
                    values.expected_response,
                    values.call_id,
                    values.description,
                    selectedChannel
                  )
                }
                disabled={isPromptTestDisabled}
                disabledTooltip={
                  isPromptPairEmpty
                    ? 'You must add a call id and an expected response'
                    : 'You must save the scorecard before setting up prompt testing'
                }
              >
                Test Pair
              </Button>
            </div>
          </div>
          <div>
            {promptTests.map((promptTest) => (
              <div key={promptTest.id} className="prompt-test flex-align-center small-gap">
                <Field
                  name={`call_id_${formatInteractionId(promptTest)}`}
                  component={TextField}
                  placeholder={formatInteractionId(promptTest)}
                  className="call-id-field"
                  disabled
                />
                <div>.....</div>
                <Field
                  name={`expected_response_${promptTest.id}`}
                  component={TextField}
                  disabled
                  placeholder={promptTest.expected_response}
                />
                <div>.....</div>
                <DescriptionField promptTest={promptTest} promptId={values.prompt_id} />
                <Button
                  critical
                  onClick={() => handleDeletePromptTest(promptTest.id)}
                  disabled={isOutdatedScorecard}
                >
                  Remove
                </Button>
                <Button
                  brand
                  onClick={() =>
                    handleSinglePromptTest(
                      values.prompt_id,
                      promptTest.expected_response,
                      formatInteractionId(promptTest),
                      promptTest.description,
                      promptTest.inputs.call_type || 'call'
                    )
                  }
                  disabled={isOutdatedScorecard}
                >
                  Test Pair
                </Button>
              </div>
            ))}
          </div>

          <Button
            onClick={handleTestCopilotPrompt}
            className="copilot-prompt__run-test"
            disabled={isEmpty(promptTests) || isOutdatedScorecard}
            disabledTooltip="Create a test first"
          >
            Run Test
          </Button>
          <div>
            {promptTestsLoading ? (
              <div className="empty-copilot-response">
                <Loader />
              </div>
            ) : (
              copilotTestResponse && (
                <div className="copilot-response-holder">
                  {copilotTestResponse
                    .sort((resp) => resp.responses_match)
                    .map((response) => (
                      <div
                        className={classNames('copilot-response', {
                          success: response.responses_match,
                          failure: !response.responses_match,
                        })}
                        key={response.prompt_test_id}
                      >
                        <div>
                          <strong>Inputs: </strong>
                          <br />
                          {response.inputs?.call_type && response.inputs?.call_type !== 'call'
                            ? response.inputs?.message_id && (
                                <>
                                  <strong>Message ID:</strong>{' '}
                                  <ExternalLink
                                    url={`/messages/${response.inputs.message_id}`}
                                    label={response.inputs.message_id}
                                  />
                                </>
                              )
                            : response.inputs?.call_id && (
                                <>
                                  <strong>Call ID:</strong>{' '}
                                  <ExternalLink
                                    url={`/calls/${response.inputs.call_id}`}
                                    label={response.inputs.call_id}
                                  />
                                </>
                              )}
                        </div>

                        <div>
                          <strong>Expected Response: </strong>
                          <br />
                          <span>{response.expected_response}</span>
                        </div>
                        <div>
                          <strong>Actual Response: </strong>
                          <br />
                          <pre>{getActualResponse(response.actual_response)}</pre>
                        </div>
                        <div>
                          <strong>Tokens Used: </strong> <span>{response.tokens_used}</span>
                        </div>
                        {!isNil(response.fallback_used) && (
                          <div>
                            <strong>Fallback Used: </strong>{' '}
                            <span>{response.fallback_used ? 'True' : 'False'}</span>
                          </div>
                        )}
                        <div>
                          <strong>Model Used: </strong> <span>{response.executed_model}</span>
                        </div>
                      </div>
                    ))}
                </div>
              )
            )}
          </div>
        </div>
      </CollapsibleSection>
    ) : null

  return (
    <form
      className="qa-copilot-editor__scorecard-criteria qa-copilot-edit-form"
      onSubmit={handleSubmit}
    >
      <header className="copilot-prompt__header-fields">
        <div className="copilot-prompt__title-field">
          <div className="label-style">Title</div>
          <Field
            name="name"
            component={TextField}
            placeholder="Name this criterion"
            type="text"
            className="input-title"
            autoFocus={isTemporaryCriteria || focusArea === ''}
            disabled={isOutdatedScorecard}
          />
        </div>
        <div className="copilot-prompt__scoring-type">
          <LabelWithHelp
            label="Scoring type"
            help={
              <>
                <p className="copilot-prompt__tooltip-lead">How this criterion gets scored:</p>
                <ul className="copilot-prompt__tooltip-list">
                  <li>
                    <strong>AI</strong> — Copilot scores this criterion automatically with an LLM,
                    using the prompt.
                  </li>
                  <li>
                    <strong>Manual</strong> — a person on your team reviews each call and scores it.
                  </li>
                </ul>
              </>
            }
          />
          <Field
            name="criteria_type"
            component={Select}
            placeholder="Select"
            options={criteriaTypeOptions.filter((option) =>
              !flags.enableManualQa ? option.value !== 'manual' : true
            )}
            isDisabled={isOutdatedScorecard}
            isClearable={false}
          />
        </div>
      </header>
      <div className="qa-copilot-editor__scorecard-criteria--content">
        <div className="qa-copilot-editor__scorecard-criteria--description">
          <h3 className="copilot-prompt__header">
            Description
            <InteractiveTooltip
              content="For your team's reference — this is not sent to the AI."
              theme="light"
              narrow
            >
              <IconInfoCircle className="icon-svg copilot-prompt__help-icon" />
            </InteractiveTooltip>
          </h3>
          <Field
            name="description"
            component={TextArea}
            placeholder="Describe this criterion and any guidelines for how to evaluate it"
            rows={2}
            disabled={isOutdatedScorecard}
          />
        </div>

        {values.criteria_type === 'manual' && (
          <Card>
            <h3>Evaluation Input</h3>
            <p>
              When evaluating a call using this assessment guideline, analysts will have three
              options to choose from:
            </p>
            <div className="flex-align-center medium-gap">
              <Banner success style={{ flex: 1, margin: 0 }}>
                Pass
              </Banner>
              <Banner critical style={{ flex: 1, margin: 0 }}>
                Fail
              </Banner>
              <Banner style={{ flex: 1, margin: 0 }}>N/A</Banner>
            </div>
          </Card>
        )}

        {values.criteria_type === 'manual' && (
          <div className="flex-align-center small-gap additional-details-holder mb">
            <div>
              <div className="label-style flex-align-center medium-gap">
                Timestamp Marker
                <Pill magical small icon={<IconSparkles />}>
                  AI
                </Pill>
              </div>
              <p className="muted-text">
                Use AI to pinpoint and jump to key moments in the conversation. It automatically
                marks when specific events or topics are mentioned in the transcript, making it
                easier for a human to evaluate the current criterion.
              </p>
            </div>
            <Field
              name="manual_timestamp_markers"
              component={RadioGroup}
              disabled={isOutdatedScorecard}
              options={[
                { value: 'true', text: 'Yes' },
                { value: 'false', text: 'No' },
              ]}
            />
          </div>
        )}

        {promptsEnabled && (
          <div className="copilot-prompt__prompt-group">
            {values.criteria_type === 'ai' && <h3 className="copilot-prompt__header">Prompt</h3>}

            {isBaltoAdmin && values.criteria_type === 'ai' && (
              <SegmentedControl
                className="copilot-prompt__mode"
                items={[
                  {
                    accessor: 'self',
                    label: 'Write it myself',
                    active: promptMode === 'self',
                    onClick: () => setPromptMode('self'),
                  },
                  {
                    accessor: 'ai',
                    label: 'Write the prompt with AI',
                    active: promptMode === 'ai',
                    onClick: () => setPromptMode('ai'),
                  },
                ]}
              />
            )}

            {showGeneratePanel ? (
              <div className="copilot-prompt__ai-generate">
                <p>
                  Answer a few questions and we&apos;ll write the prompt for you. This replaces
                  writing it yourself — you can edit the result afterward.
                </p>
                <Button
                  brand
                  onClick={handleOpenPromptGenerationModal}
                  disabled={isOutdatedScorecard}
                >
                  <IconSparkles /> Start
                </Button>
              </div>
            ) : (
              <div className="qa-copilot-editor__scorecard-criteria--user-prompt">
                <Field
                  name="user_prompt"
                  component={TextArea}
                  placeholder="Write your prompt here. Use $transcript where the call transcript should be inserted."
                  rows={null}
                  autoFocus={!isTemporaryCriteria && focusArea === 'prompt'}
                  disabled={isOutdatedScorecard}
                />
              </div>
            )}

            {/* Below the textarea, not in the mode control above it. "Write it myself" and "Write with AI" are two
                SOURCES for the text; optimizing is a TRANSFORM that needs text to already exist, so putting it up
                there would imply a third way to start from nothing. */}
            {isBaltoAdmin && values.criteria_type === 'ai' && !showGeneratePanel && (
              <OptimizePromptButton
                values={values}
                criteria={criteria}
                savedPrompt={savedPrompt}
                setFieldValue={setFieldValue}
              />
            )}

            {isBaltoAdmin && values.criteria_type !== 'ai' && (
              <div className="qa-copilot-autogenerate-prompt flex-end">
                <Button
                  brand
                  onClick={handleOpenPromptGenerationModal}
                  disabled={isOutdatedScorecard}
                >
                  <IconSparkles /> Create
                </Button>
              </div>
            )}

            {isBaltoAdmin && (
              <QACopilotPromptGenerationModal
                setFieldValue={setFieldValue}
                criterionId={values.scid}
              />
            )}

            {values.criteria_type === 'ai' && (
              <div className="copilot-prompt__expected-response">
                <h3 className="copilot-prompt__header">
                  Passing answer
                  <InteractiveTooltip
                    content={
                      <>
                        <p className="copilot-prompt__tooltip-lead">
                          Pick which answer counts as passing:
                        </p>
                        <ul className="copilot-prompt__tooltip-list">
                          <li>
                            The prompt must answer <strong>Yes</strong>, <strong>No</strong>, or{' '}
                            <strong>N/A</strong> — but N/A can never be the passing answer.
                          </li>
                          <li>
                            “Did the agent verify the customer’s identity?” → <strong>Yes</strong>{' '}
                            passes.
                          </li>
                          <li>
                            “Did the agent use inappropriate language?” → <strong>No</strong>{' '}
                            passes.
                          </li>
                        </ul>
                      </>
                    }
                    theme="light"
                    narrow
                  >
                    <IconInfoCircle className="icon-svg copilot-prompt__help-icon" />
                  </InteractiveTooltip>
                </h3>
                <div className="flex-align-center small-gap copilot-prompt__pass-line">
                  <span>This criterion passes when the prompt answers</span>
                  <div className="copilot-prompt__pass-select">
                    <Field
                      name="must_be_present"
                      component={Select}
                      options={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                      isClearable={false}
                      disabled={isOutdatedScorecard}
                    />
                  </div>
                  {errors.must_be_present && <ErrorMessage content={errors.must_be_present} />}
                </div>
              </div>
            )}
          </div>
        )}

        {promptsEnabled && (
          <div className="copilot-prompt__model-group">
            <h3 className="copilot-prompt__header">Model</h3>
            <div className="copilot-prompt__control">
              <Field
                name="llm_model_id"
                component={LlmModelSelect}
                options={getSelectableModelOptions(qaCopilotModelOptions, {
                  isNew: isTemporaryCriteria,
                })}
                loading={modelOptionsLoading}
                disabled={isOutdatedScorecard}
              />
              {errors.llm_model_id && <ErrorMessage content={errors.llm_model_id} />}
            </div>
            <ModelDeprecatedNotice show={modelIsDeprecated} />
          </div>
        )}

        {!isTemporaryCriteria && testingSection(true)}

        {promptsEnabled && <div className="copilot-prompt__section-divider">More options</div>}

        {promptsEnabled && (
          <CollapsibleSection
            id="transcript"
            title="Transcript"
            expanded={expandedSections.includes('transcript')}
            onToggle={handleToggleSection}
          >
            <div className="copilot-prompt__additional-details">
              <div className="flex-align-center small-gap additional-details-holder">
                <LabelWithHelp
                  label="Which side of the conversation"
                  help="Which speaker's lines to send to the AI."
                />
                <Field
                  name="side"
                  component={Select}
                  options={[
                    { value: 'both', label: 'Both' },
                    { value: 'agent', label: 'Agent Only' },
                    { value: 'customer', label: 'Customer Only' },
                  ]}
                  disabled={isOutdatedScorecard}
                />
              </div>
            </div>
            <div className="copilot-prompt__additional-details">
              <div className="flex-align-center small-gap additional-details-holder">
                <LabelWithHelp
                  label="Only score part of the call"
                  help="Limit scoring to the first and/or last few minutes. Leave blank to use the whole call."
                />
                <div className="flex-align-center small-gap">
                  <Field
                    placeholder="Minutes at the start"
                    name="first_x_minutes"
                    component={NumberField}
                    disabled={isOutdatedScorecard}
                  />
                  <Field
                    placeholder="Minutes at the end"
                    name="last_x_minutes"
                    component={NumberField}
                    disabled={isOutdatedScorecard}
                  />
                </div>
              </div>
            </div>
            <TimestampSilenceFields disabled={isOutdatedScorecard} />
          </CollapsibleSection>
        )}

        {promptsEnabled &&
          (values.criteria_type === 'ai' || flags?.scorecardCriteriaEmailNotificationEnabled) && (
            <CollapsibleSection
              id="onfailure"
              title="On failure"
              expanded={expandedSections.includes('onfailure')}
              onToggle={handleToggleSection}
            >
              {values.criteria_type === 'ai' && (
                <>
                  <div className="copilot-prompt__additional-details">
                    <div className="flex-align-center small-gap additional-details-holder">
                      <LabelWithHelp
                        label="When a call fails this criterion, create a task for"
                        help={
                          <>
                            <p className="copilot-prompt__tooltip-lead">The two task types:</p>
                            <ul className="copilot-prompt__tooltip-list">
                              <li>
                                <strong>Coaching</strong> — collects evidence before surfacing to a
                                manager.
                              </li>
                              <li>
                                <strong>Compliance</strong> — surfaces immediately.
                              </li>
                            </ul>
                          </>
                        }
                      />
                      <Field
                        name="tasks_created_on_miss"
                        component={MultiSelect}
                        condenseOptions={false}
                        showFullOptions
                        placeholder="Coaching and/or Compliance"
                        options={[
                          { value: 'coaching', label: 'Coaching' },
                          { value: 'compliance', label: 'Compliance' },
                        ]}
                        isDisabled={isOutdatedScorecard}
                      />
                    </div>
                  </div>
                  {showsCoachingFields && (
                    <>
                      <div className="copilot-prompt__additional-details">
                        <div className="flex-align-center small-gap additional-details-holder">
                          <LabelWithHelp
                            label="Misses before a coaching task is created"
                            help="A coaching task waits until the agent misses this criterion this many times, then surfaces to the manager. Compliance tasks surface immediately."
                          />
                          <Field
                            placeholder="3"
                            name="coaching_failure_threshold"
                            component={NumberField}
                            disabled={isOutdatedScorecard}
                          />
                        </div>
                      </div>
                      <div className="copilot-prompt__additional-details">
                        <div className="flex-align-center small-gap additional-details-holder">
                          <LabelWithHelp
                            label="Max examples per coaching task"
                            help="Once a coaching task collects this many example calls, the next miss starts a fresh task. Blank means unlimited."
                          />
                          <Field
                            placeholder="Unlimited"
                            name="max_task_evidence"
                            component={NumberField}
                            disabled={isOutdatedScorecard}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {flags?.scorecardCriteriaEmailNotificationEnabled && (
                <div className="copilot-prompt__additional-details">
                  <div className="flex-align-center small-gap additional-details-holder">
                    <LabelWithHelp label="When a call fails this criterion, send an email to these addresses" />
                    <div className="email-field">
                      <Field
                        placeholder="name@company.com,other@company.com"
                        name="email_on_failure"
                        component={TextField}
                        description="Add one or more addresses, separated by commas."
                      />
                    </div>
                  </div>
                </div>
              )}
            </CollapsibleSection>
          )}

        {promptsEnabled && values.criteria_type === 'ai' && (
          <CollapsibleSection
            id="advanced"
            title="Advanced"
            expanded={expandedSections.includes('advanced')}
            onToggle={handleToggleSection}
          >
            <div className="copilot-prompt__additional-details">
              <div className="flex-align-center small-gap additional-details-holder">
                <LabelWithHelp
                  label="Fallback strategy"
                  help="Advanced model-routing. Best left alone unless a Balto prompt engineer has asked you to change it."
                />
                <div className="flex-column medium-gap">
                  <div className="copilot-prompt__fallback-line">
                    <span>Fallback if score is</span>
                    <Field
                      name="scores_to_fallback"
                      component={MultiSelect}
                      condenseOptions={false}
                      showFullOptions
                      placeholder="Any of Yes/No/NA"
                      options={[
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                        { value: 'n/a', label: 'N/A' },
                      ]}
                      isDisabled={isOutdatedScorecard}
                    />
                  </div>
                  {!isEmpty(values.scores_to_fallback) && (
                    <div className="copilot-prompt__fallback-line">
                      <span>Run</span>
                      {/* Unlike the primary model, the fallback model is intentionally not
                          deprecation-gated here — gating it is a deferred follow-up. */}
                      <Field
                        name="fallback_initial_model_id"
                        component={LlmModelSelect}
                        options={copilotFallbackModelOptions}
                        loading={modelOptionsLoading}
                        disabled={isOutdatedScorecard}
                      />
                      <span>first</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {isTemporaryCriteria && testingSection(false)}
      </div>
      <footer>
        <div className="flex vertical-center small-gap">
          <Button onClick={handleCancel}>
            {isOutdatedScorecard ? 'Back' : isTemporaryCriteria ? 'Discard' : 'Discard Changes'}
          </Button>
          <Button
            brand
            type="submit"
            disabled={!isEmpty(errors) || isOutdatedScorecard || modelIsDeprecated}
            disabledTooltip={
              modelIsDeprecated
                ? 'Switch to a supported model to save'
                : 'Must fix errors to submit'
            }
            data-testid="add-criterion-form-button"
          >
            {isTemporaryCriteria ? 'Add Criterion' : 'Update Criterion'}
          </Button>
        </div>
      </footer>
    </form>
  )
}

export const QACopilotScorecardCriteriaPromptForm = withLDConsumer()(
  QACopilotScorecardCriteriaPromptFormComponent
)
